import { getCartErrorMessage } from '@theme/cart-feedback';

class SProductForm extends HTMLElement {
  #ac;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;

  #termsObserver;
  #frequencyId;
  // A size counts as chosen only after the shopper taps one, or when they came back from the cart's
  // "Change size or plan" link (it carries selling_plan). Ad and homepage visits start with no size.
  #sizePicked = new URLSearchParams(window.location.search).has('selling_plan');

  #setText(element, text) {
    if (element && text && element.textContent !== text) element.textContent = text;
  }

  // Keeps every price line, the order summary and the buy buttons in step with the chosen plan, hand, size and frequency.
  #syncTerms() {
    const state = this.querySelector('[data-subscription-state]');
    if (!state) return;
    if (!this.#sizePicked) {
      for (const input of this.querySelectorAll('[data-size-picker] input[type="radio"]')) input.checked = false;
    }
    const radios = [...this.querySelectorAll('input[name="selling_plan"]')];
    const remembered = radios.find(input => input.value === this.#frequencyId);
    if (remembered) remembered.checked = true;
    const selected = radios.find(input => input.checked);
    const label = selected?.closest('label')?.textContent.trim().toLowerCase() || '';
    const frequency = label.includes('3') ? state.dataset.threeMonths : label.includes('2') ? state.dataset.twoMonths : state.dataset.month;
    const text = state.dataset.terms.replace('[first]', state.dataset.first).replace('[price]', state.dataset.price).replace('[frequency]', frequency);
    for (const host of document.querySelectorAll('[data-subscription-terms]')) {
      this.#setText(host.querySelector('p') || host, text);
    }
    this.#setText(this.querySelector('[data-summary-price]'), state.dataset.priceLine?.replace('[frequency]', frequency));
    const hasSizePicker = Boolean(this.querySelector('[data-size-picker]'));
    const items = this.#sizePicked || !hasSizePicker ? state.dataset.items : state.dataset.itemsNoSize;
    this.#setText(this.querySelector('[data-summary-items]'), items);
    for (const label of document.querySelectorAll('[data-cta-label]')) this.#setText(label, state.dataset.cta);
    for (const today of document.querySelectorAll('[data-bar-today]')) this.#setText(today, state.dataset.today);
    this.#setText(this.querySelector('[data-frequency-current]'), selected?.closest('label')?.textContent.trim());
    this.toggleAttribute('data-size-picked', !this.#sizeMissing());
  }

  #sizeMissing() {
    return !this.#sizePicked && Boolean(this.querySelector('[data-size-picker]'));
  }

  #askForSize() {
    const step = this.querySelector('[data-size-step]');
    const error = this.querySelector('[data-size-error]');
    if (error) error.hidden = false;
    if (!step) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    step.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    step.focus({ preventScroll: true });
  }

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.addEventListener('change', (event) => {
      if (event.target.name === 'selling_plan') {
        this.#frequencyId = event.target.value;
        const toggle = this.querySelector('[data-frequency-toggle]');
        if (toggle) toggle.checked = false;
      }
      if (event.target instanceof Element && event.target.closest('[data-size-picker]')) {
        this.#sizePicked = true;
        const error = this.querySelector('[data-size-error]');
        if (error) error.hidden = true;
      }
      this.#syncTerms();
    }, opts);
    this.#termsObserver = new MutationObserver(() => this.#syncTerms());
    this.#termsObserver.observe(this, { childList: true, subtree: true });
    // The hero's price block re-renders on its own partial, so watch it too.
    const hero = document.querySelector('[data-step-one]');
    if (hero) this.#termsObserver.observe(hero, { childList: true, subtree: true });
    this.#syncTerms();

    this.addEventListener('submit', (event) => {
      if (this.#sizeMissing()) {
        event.preventDefault();
        this.#askForSize();
        return;
      }

      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      this.#submit(event.target, event.submitter);
    }, opts);
  }

  disconnectedCallback() {
    this.#termsObserver?.disconnect();
    this.#ac?.abort();
    this.#abortController?.abort();
    this.#setSubmitting(false);
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    // If Standard Actions are unavailable, leave the Shopify product form native.
    if (!window.Shopify?.actions?.updateCart) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  async #submit(form, submitter) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const line = this.#lineFromFormData(new FormData(form));
    if (!line) {
      this.#abortController = null;
      return;
    }

    this.#setSubmitting(true, submitter);
    this.#showError('');

    try {
      // One glove plan per order: a new pick (e.g. from "Change size or plan" in the cart) replaces
      // any line of this product already in the cart, since size and plan can't be edited in checkout.
      const staleLines = await this.#existingLinesForProduct(signal);
      this.#throwIfAborted(signal);
      if (staleLines.length) {
        await window.Shopify.actions.updateCart(
          { lines: staleLines.map((id) => ({ id, quantity: 0 })) },
          { signal }
        );
        this.#throwIfAborted(signal);
      }

      const result = await window.Shopify.actions.updateCart(
        { lines: [line] },
        {
          event: {
            context: 'product',
            detail: this.#eventDetail(),
          },
          signal,
        }
      );

      this.#throwIfAborted(signal);

      const errorMessage = this.#resultErrorMessage(result);
      if (errorMessage) {
        this.#showError(errorMessage);
      } else {
        // Keep the selected variant, selling plan and properties in the cart, then
        // leave the product page for checkout rather than opening an overlay.
        const root = window.Shopify?.routes?.root || '/';
        window.location.assign(result?.cart?.checkoutUrl || `${root}checkout`);
      }
    } catch (error) {
      if (signal.aborted) return;

      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        if (this.#abortController?.signal === signal) this.#abortController = null;
      }
    }
  }

  async #existingLinesForProduct(signal) {
    const productId = Number(this.dataset.productId);
    if (!productId) return [];
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, signal });
      if (!response.ok) return [];
      const cart = await response.json();
      return (cart.items || []).filter((item) => item.product_id === productId).map((item) => item.key);
    } catch (error) {
      if (signal.aborted) throw error;
      return [];
    }
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  #setSubmitting(submitting, submitter = this.#activeSubmitter) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('opacity-50', 'cursor-wait');
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}
